import { useEffect, useRef, useState } from "react";
import { FileText, X, Share2, Download, Printer, Loader2, Eye, EyeOff, ListTree } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { fmtDate } from "@/lib/format";
import { COMPANY } from "@/lib/company";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import PdfPagesView from "@/components/PdfPagesView";
import { printPdf } from "@/lib/quotePdf";
import { buildZReportPdf, zReportFilename } from "@/lib/zReportPdf";

const Chip = ({ on, onClick, Icon, children, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={cn("inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-bold transition-colors", on ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-slate-200 text-muted-foreground hover:border-emerald-300")}>
    <Icon className="h-3.5 w-3.5" />{children}
  </button>
);

// Gün Sonu Raporu PDF önizleme: ekranda görünen = indirilen = yazdırılan (vektörel). Kâr/maliyet ve kalem ayrıntısı anahtarları.
export function ZReportPreviewDialog({ z, onClose }) {
  const [showProfit, setShowProfit] = useState(true);
  const [showItems, setShowItems] = useState(true);
  const [pdfData, setPdfData] = useState(null);
  const [busy, setBusy] = useState(false);
  const pdfRef = useRef(null);
  const width = Math.min(720, (typeof window !== "undefined" ? window.innerWidth : 800) - 72);

  useEffect(() => {
    if (!z) { setPdfData(null); pdfRef.current = null; return; }
    let cancelled = false;
    setBusy(true);
    const t = setTimeout(async () => {
      try {
        const pdf = await buildZReportPdf(z, { showProfit, showItems });
        if (cancelled) return;
        pdfRef.current = pdf;
        setPdfData(pdf.output("arraybuffer"));
      } catch (e) { console.error(e); if (!cancelled) toast.error("Rapor PDF'i oluşturulamadı"); }
      finally { if (!cancelled) setBusy(false); }
    }, 60);
    return () => { cancelled = true; clearTimeout(t); };
  }, [z, showProfit, showItems]);

  const ready = () => { if (!pdfRef.current) { toast.error("Rapor henüz hazır değil"); return null; } return pdfRef.current; };
  const doDownload = () => { const pdf = ready(); if (!pdf) return; pdf.save(zReportFilename(z)); toast.success("Rapor indirildi"); };
  const doPrint = () => { const pdf = ready(); if (!pdf) return; printPdf(pdf, `Gün Sonu Raporu ${fmtDate(z.date)}`, { asFileName: true }); };
  const doShare = async () => {
    const pdf = ready(); if (!pdf) return;
    try {
      const blob = pdf.output("blob");
      const file = new File([blob], zReportFilename(z), { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title: "Gün Sonu Raporu", text: `${COMPANY.name} — Gün Sonu Raporu ${fmtDate(z.date)}` }); return; }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Rapor PDF olarak indirildi — dilediğiniz uygulamadan paylaşabilirsiniz");
    } catch (e) { console.error(e); toast.error("Paylaşım başarısız"); }
  };

  return (
    <Dialog open={!!z} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[98vw] sm:max-w-4xl max-h-[95vh] overflow-hidden flex flex-col" data-testid="qs-z-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-xl flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />Gün Sonu Raporu
            {z && <span className="font-mono text-sm font-bold text-primary bg-primary/10 rounded px-1.5 py-0.5" data-testid="qs-z-preview-date">{fmtDate(z.date)}</span>}
          </DialogTitle>
        </DialogHeader>
        {z && (
          <>
            <div className="flex flex-wrap items-center gap-2 pb-2" data-testid="qs-z-pv-opts">
              <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Rapor içeriği:</span>
              <Chip on={showProfit} onClick={() => setShowProfit((v) => !v)} Icon={showProfit ? EyeOff : Eye} testid="qs-z-pv-profit">Kâr / Maliyet</Chip>
              <Chip on={showItems} onClick={() => setShowItems((v) => !v)} Icon={ListTree} testid="qs-z-pv-items">Sepet kalemleri</Chip>
              <span className="ml-auto text-[11px] text-muted-foreground">{z.totals?.count || 0} fiş</span>
            </div>
            <div className="flex-1 min-h-0 overflow-auto rounded-lg bg-[radial-gradient(circle_at_top,_#e2e8f0_0%,_#cbd5e1_100%)] p-3 sm:p-5" data-testid="qs-z-pv-body">
              {pdfData ? <PdfPagesView data={pdfData} width={width} testid="qs-z-pv-pdf" />
                : <div className="flex items-center justify-center py-24 text-sm text-muted-foreground" data-testid="qs-z-pv-loading"><Loader2 className="h-5 w-5 animate-spin mr-2" />Rapor hazırlanıyor…</div>}
            </div>
            <div className="flex flex-wrap justify-end gap-2 pt-3">
              <Button variant="outline" onClick={onClose} data-testid="qs-z-pv-close"><X className="h-4 w-4 mr-1.5" />Kapat</Button>
              <Button variant="secondary" onClick={doShare} disabled={busy || !pdfData} data-testid="qs-z-pv-share"><Share2 className="h-4 w-4 mr-1.5" />Paylaş</Button>
              <Button variant="secondary" onClick={doDownload} disabled={busy || !pdfData} data-testid="qs-z-pv-download">{busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Download className="h-4 w-4 mr-1.5" />}İndir</Button>
              <Button onClick={doPrint} disabled={busy || !pdfData} data-testid="qs-z-pv-print"><Printer className="h-4 w-4 mr-1.5" />Yazdır</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
